import { inject, type InjectionKey } from 'vue'

/**
 * Minimal user-preferences shape that vue-components primitives need.
 *
 * Owned by this package — do **not** import the lib's full
 * `UserPreferences` type here. The app is responsible for mapping its
 * richer shape down to this minimal interface when it calls
 * `provideUserPreferences()`. Keeping the shape minimal is what lets
 * vue-components stay free of `@gat-ship/lib`.
 *
 * Both fields are optional: when no provider is wired (e.g. unit tests,
 * storybook, a future standalone vue-components consumer) `useUserPreferences()`
 * returns an empty object and consumers fall back to their own defaults.
 */
export interface UserPreferences {
  /**
   * Locale string for date formatting (e.g. `'en-US'`, `'nb-NO'`).
   * When undefined, the date inputs fall back to
   * `Intl.DateTimeFormat().resolvedOptions().locale`.
   */
  dateFormat?: string
  /**
   * Default modal type. When undefined, `modal.vue` falls back to `'drawer'`.
   */
  modalType?: 'dialog' | 'drawer'
}

/**
 * Getter signature so the provider can react to user-pref changes
 * without having to re-provide. Each consumer call invokes the getter
 * fresh, which preserves Vue reactivity when called inside `computed()`.
 */
export type UserPreferencesGetter = () => UserPreferences

export const userPreferencesInjectionKey: InjectionKey<UserPreferencesGetter> =
  Symbol('gs-user-preferences')

const EMPTY_PREFS: UserPreferences = {}
const EMPTY_GETTER: UserPreferencesGetter = () => EMPTY_PREFS

/**
 * Read current user preferences from the inject context. Safe to call
 * outside a provider — returns an empty preferences object so consumers
 * can fall back to their own defaults.
 *
 * Must be called during setup. Returns a getter that can be invoked
 * inside `computed()` to react to provider-side changes.
 */
export function useUserPreferences(): UserPreferencesGetter {
  const getter = inject(userPreferencesInjectionKey, EMPTY_GETTER)
  return getter
}
